import { create } from 'zustand';

import type { DeviceStatus } from './constants';
import { ACTION_FILTER_QUEUED } from './constants';
import type { DeviceFilters } from './types';

/**
 * Session state for the Devices grid: the selected category tab, the search term, and the
 * applied filters (US-112439 §11). These are the query inputs the backend filters on.
 *
 * Why Zustand and not `useState` in the page, and not the URL:
 *
 * - The AC wants this state to **survive in-session navigation** ("I search, scroll, or navigate
 *   within the Devices screen") but to be **cleared by a page reload** ("cold start / full
 *   browser refresh"). A store outside the route tree survives the page unmounting when the user
 *   opens a device and comes back; because it is plain in-memory state with no persistence
 *   middleware, a reload starts it empty. `useState` in the page would lose it on that
 *   navigation; the URL / `sessionStorage` would wrongly survive the reload.
 * - It holds no server data (ADR: Zustand is client/UI state only) — just the query inputs that
 *   TanStack Query keys off.
 */
export const EMPTY_DEVICE_FILTERS: DeviceFilters = {
  status: [],
  subscriptionStatus: [],
  action: [],
};

type DevicesUiState = {
  /** Selected quick-filter tab: a category id from `meta/categories`, or null for "All". */
  categoryId: string | null;
  search: string;
  filters: DeviceFilters;
  setCategoryId: (categoryId: string | null) => void;
  setSearch: (search: string) => void;
  applyFilters: (filters: DeviceFilters) => void;
  clearFilters: () => void;
  /** Start over and show one status, or everything for `null` — the Profile panel counters. */
  showOnly: (status: DeviceStatus | null) => void;
  /** Full reset — used by tests and by any future "start over" affordance. */
  reset: () => void;
};

const INITIAL = {
  categoryId: null,
  search: '',
  filters: EMPTY_DEVICE_FILTERS,
} satisfies Pick<DevicesUiState, 'categoryId' | 'search' | 'filters'>;

export const useDevicesUiStore = create<DevicesUiState>((set) => ({
  ...INITIAL,
  setCategoryId: (categoryId) => set({ categoryId }),
  setSearch: (search) => set({ search }),
  applyFilters: (filters) => set({ filters }),
  clearFilters: () => set({ filters: EMPTY_DEVICE_FILTERS }),
  showOnly: (status) =>
    set({ ...INITIAL, filters: { ...EMPTY_DEVICE_FILTERS, status: status ? [status] : [] } }),
  reset: () => set(INITIAL),
}));

/** True when at least one chip is selected — drives the filter icon's active dot. */
export function hasActiveFilters(filters: DeviceFilters): boolean {
  return (
    filters.status.length > 0 || filters.subscriptionStatus.length > 0 || filters.action.length > 0
  );
}

/** Toggle one chip within one group, returning a new filters object (never mutating). */
export function toggleFilter<G extends keyof DeviceFilters>(
  filters: DeviceFilters,
  group: G,
  value: DeviceFilters[G][number],
): DeviceFilters {
  const current = filters[group] as DeviceFilters[G][number][];
  const next = current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value];
  return { ...filters, [group]: next };
}

/** Re-exported so the panel can reference the single Action chip value without a raw string. */
export { ACTION_FILTER_QUEUED };
